import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { PROXY_WS_DEFAULT, resolveProxyWsUrl, withWsToken, refreshCredentialOnAuthClose } from '../utils/proxyWs';
import { refreshProxyCredential } from '../api/proxyAuth';
import { takeoverNotice } from '../utils/isolationCopy';
import {
    applyTwoTier,
    stripBodies,
    FULL_BODY_KEEP,
    HISTORY_KEEP,
    buildSlimSnapshot as buildSlim,
    reconcileGroupsWithSnapshot,
} from '../utils/liveHistory';
import { loadBoundary, saveBoundary, clearBoundary } from '../utils/sessionBoundary';

const TrafficContext = createContext(null);

export const useTraffic = () => useContext(TrafficContext);

// docs/signals-recommendations-watch-spec.md §6 — bounded (50) newest-first
// watch-alert ring. Module-level (not component-local) so it can back both
// the WS handler below AND applyWatchAlert(), which is exported as a pure
// function purely so the bounded/dedup ingestion logic is unit-testable
// without a React-render harness (@testing-library isn't installed in this
// project and package.json is out of scope for this work package — see
// TrafficContext.watch.test.js for the coverage this buys).
const WATCH_ALERTS_MAX = 50;

// Per-user traffic isolation (docs/sdlc/per-user-traffic-isolation, AC 119). The proxy now
// delivers history_cleared / history_snapshot PER VIEWER, so these two decisions are
// pure and unit-tested (TrafficContext.clear.test.js) instead of living inline in the
// WS handler.
//   - history_cleared with a deviceId clears that one group; without one it is the
//     viewer's own full clear (the proxy sent it only to this viewer's sockets).
//   - An EMPTY history_snapshot is never a wipe: a proxy restart, or a viewer who
//     simply owns no rows yet, sends one. Only history_cleared may remove state.
export function classifyHistoryCleared(data) {
    if (!data || data.type !== 'history_cleared') return 'ignore';
    return data.deviceId ? 'device' : 'all';
}
export function classifyHistorySnapshot(data) {
    if (!data || data.type !== 'history_snapshot' || !Array.isArray(data.data)) return 'ignore';
    return data.data.length === 0 ? 'empty' : 'merge';
}

const TAKEOVER_NOTICES_MAX = 5;
// Bounded newest-first list of ip_takeover notices, deduped on role + ip + at.
export function applyTakeoverNotice(prev, msg) {
    const n = takeoverNotice(msg);
    if (!n) return prev;
    const id = `${n.role}:${n.ip}:${n.at || ''}`;
    if ((prev || []).some((p) => p.id === id)) return prev;
    return [{ ...n, id }, ...(prev || [])].slice(0, TAKEOVER_NOTICES_MAX);
}

/**
 * applyWatchAlert(prevAlerts, alert, max) -> alert[]
 * Pure reducer step: prepend `alert` (newest-first), de-duped by `.id`
 * (a redelivered alert is a no-op, not a duplicate entry), truncated to
 * `max` entries. Never throws on malformed input.
 */
// The ordered effect sequence behind "New session", extracted from the hook so the
// contract is testable without a component-render harness (same reasoning as
// applyWatchAlert below). ORDER IS THE CONTRACT: stamp the boundary BEFORE asking
// the proxy to wipe, so a send that never lands still degrades to hide-don't-delete
// rather than doing nothing. Collaborators are injected; this touches no module state.
export function runNewSession({ stamp, resetLocal, send }, now) {
    stamp(now);
    resetLocal();
    send({ type: 'clear_history' }); // unscoped — every device, matching Clear all
}

export function applyWatchAlert(prevAlerts, alert, max = WATCH_ALERTS_MAX) {
    const prev = Array.isArray(prevAlerts) ? prevAlerts : [];
    if (!alert || typeof alert !== 'object') return prev;
    if (alert.id && prev.some((a) => a && a.id === alert.id)) return prev;
    const next = [alert, ...prev];
    return next.length > max ? next.slice(0, max) : next;
}

// An async Ask answer that arrived over the WS while the question's asker was
// (or wasn't) still watching — docs/checks-ask-async-spec.md. Kept as a map by
// jobId rather than a list because the only consumer looks up ONE id: the job
// it is waiting on. Small cap; each entry holds a whole answer, and the panel
// re-reads a missed one from the server by id anyway.
const ASK_JOBS_MAX = 12;

/**
 * applyAskJob(prevJobs, msg, max) -> { [jobId]: { status, result, error, ts } }
 * Pure reducer step. Never throws on malformed input; a message with no jobId
 * is dropped rather than stored under `undefined`, where the panel's lookup
 * would find it for EVERY question it waits on.
 */
export function applyAskJob(prevJobs, msg, max = ASK_JOBS_MAX) {
    const prev = (prevJobs && typeof prevJobs === 'object') ? prevJobs : {};
    if (!msg || typeof msg !== 'object' || typeof msg.jobId !== 'string' || !msg.jobId) return prev;
    const next = {
        ...prev,
        [msg.jobId]: {
            status: msg.status || 'done',
            result: msg.result || null,
            error: msg.error || null,
            ts: msg.ts || Date.now(),
        },
    };
    const ids = Object.keys(next);
    if (ids.length <= max) return next;
    // Evict oldest by arrival, keeping the newest `max`.
    const keep = new Set(ids.sort((a, b) => (next[b].ts || 0) - (next[a].ts || 0)).slice(0, max));
    const trimmed = {};
    for (const id of ids) if (keep.has(id)) trimmed[id] = next[id];
    return trimmed;
}

// ── Debounced slim-snapshot persistence ─────────────────────────────────
// Stringifying the whole slim snapshot on EVERY WS event was O(rows) per event and
// swallowed QuotaExceededError silently. This coalesces writes (default 1s), flushes
// on demand (beforeunload / unmount), and on a quota error halves the per-group cap
// and retries so the newest rows still persist, reporting it once via onQuota.
// Pure + injectable (storage, build) so it is testable without a render harness.
export const SLIM_PERSIST_DELAY_MS = 1000;
const SLIM_MIN_KEEP = 25;

function isQuotaError(e) {
    return !!e && (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED'
        || e.code === 22 || e.code === 1014 || /quota/i.test(String(e.message || '')));
}

export function createSlimPersister({ storage, key = 'groupedResults.slim', build, onQuota, initialKeep = 500, delayMs = SLIM_PERSIST_DELAY_MS } = {}) {
    let keep = initialKeep;
    let pending = null;
    let timer = null;
    const trim = (grouped) => {
        const out = {};
        for (const [k, list] of Object.entries(grouped || {})) out[k] = Array.isArray(list) ? list.slice(0, keep) : list;
        return out;
    };
    function flush() {
        if (timer) { clearTimeout(timer); timer = null; }
        if (pending == null) return;
        const grouped = pending;
        pending = null;
        for (let attempt = 0; attempt < 8; attempt++) {
            try { storage.setItem(key, JSON.stringify(build(trim(grouped)))); return; }
            catch (e) {
                if (!isQuotaError(e) || keep <= SLIM_MIN_KEEP) return;
                keep = Math.max(SLIM_MIN_KEEP, Math.floor(keep / 2));
                if (onQuota) { try { onQuota(keep); } catch { /* notice is best-effort */ } }
            }
        }
    }
    return {
        schedule(grouped) {
            pending = grouped;
            if (!timer) timer = setTimeout(flush, delayMs);
        },
        flush,
        cancel() { pending = null; if (timer) { clearTimeout(timer); timer = null; } },
        keep: () => keep,
    };
}

// ── WS reconnect backoff ────────────────────────────────────────────────
// WHY this isn't a flat setTimeout(connect, 3000): the proxy runs on a 1GB
// droplet with a documented OOM crash-loop history (docs/prod-proxy-oom.md).
// A flat 3s retry means every open dashboard tab hammers the proxy at ~20
// connection attempts/minute the instant it restarts — a stampede that can
// re-trigger the very OOM that took it down, so it flaps forever instead of
// recovering. Capped exponential backoff spaces those attempts out; jitter
// desynchronizes tabs so they don't all retry in lockstep (without jitter a
// synchronized stampede is just slower, not smaller). Do not "simplify" this
// back to a flat interval.
const RECONNECT_BASE_MS = 3000;   // unchanged from the old flat interval — a
                                   // single blip still recovers in ~3s
const RECONNECT_CEILING_MS = 30000;
const RECONNECT_JITTER = 0.2;     // ±20%

/**
 * nextReconnectDelay(attempt, rand?) -> number (ms)
 * Pure delay computation for the WS reconnect loop: `attempt` is the number
 * of consecutive failed connections since the last success (0 for the retry
 * scheduled right after the first drop). Doubles from RECONNECT_BASE_MS each
 * attempt, capped at RECONNECT_CEILING_MS, with ±RECONNECT_JITTER jitter so
 * multiple tabs/devices don't reconnect in lockstep. `rand` is injectable
 * (defaults to Math.random) so the jitter band is testable deterministically.
 * The caller is responsible for resetting `attempt` back to 0 on a
 * successful connection — this function has no memory of its own.
 */
export function nextReconnectDelay(attempt, rand = Math.random) {
    const exponent = Math.max(0, attempt || 0);
    const base = Math.min(RECONNECT_BASE_MS * Math.pow(2, exponent), RECONNECT_CEILING_MS);
    const jitterFactor = 1 + (rand() * 2 - 1) * RECONNECT_JITTER; // in [1 - JITTER, 1 + JITTER]
    return Math.round(base * jitterFactor);
}

// ─────────────────────────────────────────────────────────────────────────────
// TrafficProvider is now the app-level LIVE-TRAFFIC ENGINE. It owns the single
// proxy WebSocket, the full message switch, and the captured-row state that the
// Dashboard (pages/MainApp.js) used to own. It is mounted ONCE above the router
// (App.js: <TrafficProvider><MainLayout/></TrafficProvider>), so navigating away
// from the Dashboard no longer tears down the socket or stops row ingestion —
// capture continues across routes and the Activity strip keeps its window.
//
// Downstream pages/components read the SAME context fields as before
// (liveTrafficGroups, wsStatus, proxyWsUrl, connectedDevices, sessionId,
// clearSignal, jumpTarget, checksScopeRows). Those are now produced by this
// provider directly instead of being pushed into it by MainApp.
//
// UI-only reactions that touch Dashboard component state (auto-detect toast +
// capture session) stay in MainApp; the provider exposes subscribeMessages() so
// MainApp can react to accepted live events without owning the socket.
// ─────────────────────────────────────────────────────────────────────────────
export const TrafficProvider = ({ children }) => {
    // groupedResults: { [deviceId]: [rows...] } — the live capture store. Exposed
    // downstream as `liveTrafficGroups` (unchanged contract). Lazy init — restore
    // slim snapshot from localStorage immediately so data appears on refresh
    // before the WebSocket history_snapshot arrives.
    const [groupedResults, setGroupedResults] = useState(() => {
        try {
            const cached = localStorage.getItem('groupedResults.slim');
            return cached ? JSON.parse(cached) : {};
        } catch { return {}; }
    });

    // Global Session ID for bridged isolation (init from localStorage or auto-generate)
    const [sessionId, setSessionId] = useState(() => {
        const stored = localStorage.getItem('debug_ssid');
        // No auto-generation. Force generic login.
        if (stored) return stored;
        return null;
    });

    // clearSignal — increments every time the user clears all requests.
    // Components (LiveObserverPanel, right panel) watch this to reset their own state.
    const [clearSignal, setClearSignal] = useState(0);
    const triggerClear = useCallback(() => setClearSignal(n => n + 1), []);

    // sessionBoundary — the timestamp stamped by "New session" (see utils/sessionBoundary.js).
    // MainApp's row pipeline uses it to collapse rows captured before it behind an
    // expandable "earlier" header. Since 2026-08-11 this is the FALLBACK half of the
    // flow, not the whole of it — startNewSession (defined below, once wsSend and
    // resetAllLocalTraffic exist) now genuinely clears as well.
    const [sessionBoundary, setSessionBoundary] = useState(() => loadBoundary());
    const clearSessionBoundary = useCallback(() => {
        clearBoundary();
        setSessionBoundary(null);
    }, []);

    // jumpTarget — set by any page to auto-select a row in MainApp
    const [jumpTarget, setJumpTarget] = useState(null);

    // checksScopeRows — a SNAPSHOT of rows picked on the Dashboard (InspectraNetworkView's
    // `picked` selection), handed off to ChecksPage so Audit/Run/Discover/Suggest scope to
    // exactly those requests. Plain row objects, captured at click-time (not a live
    // reference), so later mutation of the Dashboard's own selection can't affect this.
    // Survives route navigation because TrafficProvider never unmounts. Empty array means
    // "no handoff active" — ChecksPage falls back to all captured requests.
    const [checksScopeRows, setChecksScopeRows] = useState([]);

    // ── Global proxy / WS status — now OWNED here, read by the global ribbon ──
    // 'connecting' | 'connected' | 'disconnected'
    const [wsConnectState, setWsConnectState] = useState('connecting');
    // { [deviceId]: count } — device label → request count
    const [connectedDevices, setConnectedDevices] = useState({});

    // ── Live capture state moved out of MainApp (owned here so it survives routes) ──
    const [deviceConfig, setDeviceConfig] = useState(() => {
        try {
            const cached = localStorage.getItem('deviceConfig');
            return cached ? JSON.parse(cached) : {};
        } catch { return {}; }
    });
    // Publisher Settings (GMA SDK getconfig/pubsetting) per device. Fires once at
    // app launch; we keep only the LATEST so a relaunch in the same session
    // replaces the prior integration snapshot. Keyed by device groupKey.
    const [pubSettings, setPubSettings] = useState(() => {
        try {
            const cached = localStorage.getItem('pubSettings');
            return cached ? JSON.parse(cached) : {};
        } catch { return {}; }
    });
    // ---- Per-device capture controls ----
    const [pausedDevices, setPausedDevices] = useState({});          // { [deviceId]: true }
    const [suppressedByDevice, setSuppressedByDevice] = useState({}); // { [deviceId]: number }
    const [tlsDrops, setTlsDrops] = useState({});                    // { [host]: { host, count, lastSeen, codes } }
    // Watch-mode alerts — bounded (50) newest-first ring, same shape as the
    // proxy's alertStore.list() (docs/signals-recommendations-watch-spec.md
    // §6): { id, ts, kind, severity, shadow, subject, summary, current,
    // baseline, evidence, nextStep }. Independent of captured-row state, so
    // history_snapshot merges never touch it; a full history_cleared DOES
    // wipe it (see resetAllLocalTraffic below) since it's part of "this
    // session's" local state same as tlsDrops/pubSettings.
    const [watchAlerts, setWatchAlerts] = useState([]);
    // Async Ask answers pushed while this tab was connected, by jobId. NOT wiped
    // by history_cleared the way watchAlerts is: an alert is derived from the
    // traffic being cleared, but an answer is work the user paid for.
    const [askJobs, setAskJobs] = useState({});
    // Android IP-takeover notices (WS `ip_takeover`, sent only to the two owners involved).
    const [takeoverNotices, setTakeoverNotices] = useState([]);
    const dismissTakeoverNotice = useCallback((id) => {
        setTakeoverNotices(prev => prev.filter(n => n.id !== id));
    }, []);
    // True once THIS connection has received any history_snapshot, even an empty one, so
    // the isolation empty state does not flash before the proxy has answered.
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const dismissWatchAlert = useCallback((id) => {
        setWatchAlerts(prev => prev.filter(a => a.id !== id));
    }, []);
    const clearWatchAlerts = useCallback(() => setWatchAlerts([]), []);
    const [serverIp, setServerIp] = useState(() => {
        // Auto-detect IP from browser URL immediately
        const h = window.location.hostname;
        return (h === 'localhost' || h === '127.0.0.1') ? 'localhost' : h;
    });
    // Datacenter-egress self-check result, pushed once per connection by the
    // proxy (egressCheck.js) right after server_ip. null until it arrives —
    // { datacenter, ip, org, isp, ... } | { error: true } once it does.
    const [egressStatus, setEgressStatus] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [statusMsg, setStatusMsg] = useState('');

    // Selected device tab — owned here so the WS message handler can auto-select
    // verbatim; MainApp reads/writes it via context. `selectedTabRef` mirrors the
    // value so the handler can read the freshest tab without re-running the effect.
    const [selectedTab, setSelectedTabState] = useState(() => {
        return localStorage.getItem('selectedTab') || '';
    });
    const selectedTabRef = useRef('');
    const setSelectedTab = useCallback((v) => {
        selectedTabRef.current = v; // keep ref immediately in sync (callers pass plain values)
        setSelectedTabState(v);
    }, []);

    // Proxy WebSocket URL — configurable per tester, persisted in localStorage.
    // Defaults to the permanent cloud proxy at proxy.adlensfx.com.
    const DEFAULT_PROXY_WS = PROXY_WS_DEFAULT;
    // resolveProxyWsUrl() heals stale ws://localhost overrides on https up front.
    const [proxyWsUrl, setProxyWsUrl] = useState(() => resolveProxyWsUrl());
    // eslint-disable-next-line no-unused-vars
    const [showProxyInput, setShowProxyInput] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [proxyWsInput, setProxyWsInput] = useState(() => resolveProxyWsUrl());
    // eslint-disable-next-line no-unused-vars
    const saveProxyWsUrl = (url) => {
        const trimmed = url.trim();
        try { localStorage.setItem('proxy_ws_url', trimmed); } catch {}
        setProxyWsUrl(trimmed);
        setProxyWsInput(trimmed);
        setShowProxyInput(false);
    };

    // Read pause state through a ref inside the WS handlers: it used to be an effect
    // dep, so toggling pause closed the socket, flashed "Connection lost" and replayed
    // the whole snapshot.
    const pausedDevicesRef = useRef({});
    pausedDevicesRef.current = pausedDevices;

    const [storageNotice, setStorageNotice] = useState('');
    const dismissStorageNotice = useCallback(() => setStorageNotice(''), []);
    const persisterRef = useRef(null);
    if (!persisterRef.current) {
        persisterRef.current = createSlimPersister({
            storage: { setItem: (k, v) => localStorage.setItem(k, v) },
            build: (g) => buildSlim(g, stashedRef.current),
            onQuota: (keep) => setStorageNotice(`Browser storage is full — keeping only the newest ${keep} rows per device in the local backup.`),
        });
    }
    const persistSlim = useCallback((grouped) => persisterRef.current.schedule(grouped), []);
    useEffect(() => {
        const flush = () => persisterRef.current.flush();
        window.addEventListener('beforeunload', flush);
        return () => { window.removeEventListener('beforeunload', flush); flush(); };
    }, []);

    const socketRef = useRef(null);
    // A New session started while the WS was not open (the session picker runs
    // before any socket exists). onopen sends the wipe BEFORE get_history, so the
    // proxy drops its buffer instead of replaying the previous pass's traffic.
    const pendingClearRef = useRef(false);
    const hasInitialized = useRef(false);
    // requestIds whose full bodies are already stashed to IndexedDB (dedup so we
    // don't re-write the same large bodies on every slim-snapshot save).
    const stashedRef = useRef(new Set());

    // ── Message subscription — lets the Dashboard react to accepted live events
    // (auto-detect toast + capture session) without owning the socket. Fires for
    // full history_cleared and for accepted auction events (paused-dropped events
    // are NOT delivered, preserving the original drop-before-auto-detect order).
    const listenersRef = useRef(new Set());
    const emit = useCallback((data, newEntry) => {
        listenersRef.current.forEach(fn => { try { fn(data, newEntry); } catch { } });
    }, []);
    const subscribeMessages = useCallback((fn) => {
        listenersRef.current.add(fn);
        return () => { listenersRef.current.delete(fn); };
    }, []);

    // Stable WS sender — returns true if the frame was sent, false otherwise.
    const wsSend = useCallback((obj) => {
        const sock = socketRef.current;
        if (sock && sock.readyState === WebSocket.OPEN) {
            sock.send(JSON.stringify(obj));
            return true;
        }
        return false;
    }, []);

    // Full local reset for an ALL-devices clear — the exact same reset the
    // `history_cleared` (no deviceId) WS handler below performs, extracted so
    // both the server broadcast AND the "Clear all" button's proactive local
    // reset share one code path (no drift between them). This is the
    // belt-and-suspenders half of the "Clear all" flow: the caller also sends
    // `wsSend({ type: 'clear_history' })` (no deviceId) for the server-side
    // wipe, but this runs immediately client-side too so a missed/late
    // broadcast can never strand a phantom device group in localStorage.
    const resetAllLocalTraffic = useCallback(() => {
        setGroupedResults({});
        setDeviceConfig({});
        setPubSettings({});
        setTlsDrops({});
        setWatchAlerts([]); // full history_cleared wipes the watch-alert ring too
        persisterRef.current.cancel(); // a pending debounced write must not resurrect the wiped state
        try {
            localStorage.removeItem('groupedResults.slim');
            localStorage.removeItem('deviceConfig');
            localStorage.removeItem('pubSettings');
        } catch {}
        triggerClear(); // signal LiveObserver + right panel to reset
    }, [triggerClear]);

    // "New session" — a genuine fresh start. Until 2026-08-11 this only stamped a
    // boundary (hide, don't delete); the rows stayed in localStorage AND in the
    // proxy's buffer, so "start a new session" left the previous pass's traffic on
    // screen. Per the user: launching a new session should clear everything.
    //
    // Order matters, and the stamp is deliberately kept as the fallback half:
    //   1. stamp the boundary FIRST — if the WS is down or the broadcast is missed,
    //      the proxy still holds its history and will replay it as history_snapshot
    //      on the next connect. The boundary is the only thing that keeps those
    //      replayed rows hidden, so a failed server wipe degrades to the old
    //      hide-don't-delete behavior instead of silently doing nothing.
    //   2. wipe local state immediately (don't wait on the round-trip — same
    //      phantom-device-group reasoning as the Clear all button).
    //   3. ask the proxy to drop its retained history. When it confirms with an
    //      unscoped history_cleared, that handler calls clearSessionBoundary() —
    //      correct, because by then there is genuinely nothing left to hide.
    // The confirm() lives at the button (InspectraNetworkView), matching how the
    // Clear / Clear all handlers in MainApp gate their own destructive sends.
    const startNewSession = useCallback(() => {
        runNewSession({
            stamp: (now) => { saveBoundary(now); setSessionBoundary(now); },
            resetLocal: resetAllLocalTraffic,
            send: (msg) => { if (!wsSend(msg)) pendingClearRef.current = true; },
        }, Date.now());
    }, [resetAllLocalTraffic, wsSend]);

    // ---- Helper to toggle pause for a device and notify server ----
    const togglePauseDevice = useCallback((deviceId) => {
        if (!deviceId) return;
        setPausedDevices(prev => {
            const next = { ...prev };
            const paused = !next[deviceId];
            if (paused) next[deviceId] = true; else delete next[deviceId];
            // notify server (best-effort)
            try {
                const ws = socketRef.current;
                if (ws && ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: 'control', action: 'pause_device', deviceId, paused }));
                }
            } catch { }
            // reset suppressed count on resume
            if (!paused) {
                setSuppressedByDevice(s => ({ ...s, [deviceId]: 0 }));
            }
            return next;
        });
    }, []);

    // Persist session ID changes
    useEffect(() => {
        if (sessionId) localStorage.setItem('debug_ssid', sessionId);
        else localStorage.removeItem('debug_ssid');
    }, [sessionId]);

    // ---- Persist/restore live state (selected tab + config) ----
    useEffect(() => {
        selectedTabRef.current = selectedTab;
        localStorage.setItem('selectedTab', selectedTab);
    }, [selectedTab]);

    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;
        selectedTabRef.current = selectedTab; // sync ref
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem('deviceConfig', JSON.stringify(deviceConfig));
        } catch (e) {
            console.warn('⚠️ LocalStorage full, failing gracefully:', e);
            // Optional: clear if it's too full
            // localStorage.removeItem('deviceConfig');
        }
    }, [deviceConfig]);

    useEffect(() => {
        try {
            localStorage.setItem('pubSettings', JSON.stringify(pubSettings));
        } catch (e) {
            console.warn('⚠️ LocalStorage full (pubSettings), failing gracefully:', e);
        }
    }, [pubSettings]);

    // Build a map of device label → request count for the ribbon
    useEffect(() => {
        const devices = {};
        Object.entries(groupedResults).forEach(([key, rows]) => { devices[key] = rows.length; });
        setConnectedDevices(devices);
    }, [groupedResults]);

    // ---- WebSocket for live (with per-device pause) ----
    useEffect(() => {
        // Only connect once the user is authenticated (MainApp used to guard this by
        // simply not mounting until sessionId existed). Reconnect when it changes.
        if (!sessionId) return undefined;

        let socket;
        let reconnectTimer;
        let reconnectAttempt = 0; // consecutive failed connections since the last success; drives nextReconnectDelay()
        let isUnmounting = false;

        // NOTE: this reconnect loop intentionally does NOT gate on document
        // visibility. A separate change made the Inbox *poll* visibility-gated
        // (correct there — it only refreshes a badge nobody can see on a hidden
        // tab). This socket is different: it carries live captured ad traffic,
        // and a hidden tab during a device test must keep capturing. Don't
        // "make these consistent" by pausing reconnects when the tab is hidden.

        function connect() {
            if (isUnmounting) return;

            const isSecure = window.location.protocol === 'https:';
            const host = window.location.hostname;

            // Resolve proxy WebSocket URL:
            // 1. proxyWsUrl state (defaults wss://proxy.adlensfx.com, overrideable per tester)
            // 2. Local dev HTTP fallback: ws://localhost:9000
            let wsUrl;
            if (proxyWsUrl) {
                wsUrl = proxyWsUrl;
            } else if (!isSecure) {
                wsUrl = `ws://${host}:9000`;
            } else {
                console.warn('⚠️ Running on HTTPS but no Proxy WS URL is set.');
                setStatusMsg('⚠️ Set Proxy URL to connect (see Proxy Settings button)');
                return;
            }

            // Guard: browsers block ws:// (plain) from an https:// page (mixed content).
            // If we're on HTTPS with a localStorage stale ws:// URL, auto-reset to default.
            if (isSecure && wsUrl.startsWith('ws://')) {
                console.warn('⚠️ Insecure ws:// URL on HTTPS page — resetting to default:', DEFAULT_PROXY_WS);
                try { localStorage.removeItem('proxy_ws_url'); } catch {}
                setProxyWsUrl(DEFAULT_PROXY_WS);
                setProxyWsInput(DEFAULT_PROXY_WS);
                wsUrl = DEFAULT_PROXY_WS;
            }

            console.log('🔌 Connecting to WS:', wsUrl);
            socket = new WebSocket(withWsToken(wsUrl));
            socketRef.current = socket;

            socket.onopen = () => {
                console.log('✅ WS connected');
                setWsConnectState('connected');
                setStatusMsg('✅ Connected to validation server');
                // Reset backoff on a SUCCESSFUL connection — not merely when
                // connect() is invoked. Without this reset, one bad afternoon of
                // flapping leaves every tab permanently retrying at the 30s
                // ceiling, so the next ordinary blip takes up to 30s to recover
                // instead of the ~3s it should.
                reconnectAttempt = 0;
                if (pendingClearRef.current) {
                    pendingClearRef.current = false;
                    socket.send(JSON.stringify({ type: 'clear_history' }));
                }
                // Request history on connect, sending our Session ID to bridge the IP gap
                socket.send(JSON.stringify({ type: 'get_history', ssid: sessionId }));

                // Re-send any pause states
                Object.keys(pausedDevicesRef.current).forEach(devId => {
                    socket.send(JSON.stringify({ type: 'control', action: 'pause_device', deviceId: devId, paused: true }));
                });
            };

            socket.onclose = (e) => {
                console.warn('🔌 WS closed:', e.code, e.reason);
                setWsConnectState('disconnected');
                setHistoryLoaded(false);
                setStatusMsg('⚠️ Disconnected... retrying...');

                // Retry with capped exponential backoff + jitter — see
                // nextReconnectDelay() and the comment above `reconnectAttempt`
                // for why a flat 3s retry is unsafe here (droplet OOM stampede).
                if (!isUnmounting) {
                    const delay = nextReconnectDelay(reconnectAttempt);
                    reconnectAttempt += 1;
                    // 4401 = the proxy refused/expired our credential. Mint a fresh
                    // per-user one BEFORE reconnecting (withWsToken reads the cache
                    // synchronously), so the retry authenticates without a reload.
                    // Never falls back to a static token that has been removed.
                    reconnectTimer = setTimeout(async () => {
                        await refreshCredentialOnAuthClose(e && e.code, refreshProxyCredential);
                        connect();
                    }, delay);
                }
            };

            socket.onerror = (e) => {
                console.error('❌ WS error', e);
                socket.close(); // Force close to trigger onclose logic
            };

            socket.onmessage = (msg) => {
                try {
                    const data = JSON.parse(msg.data);

                    // Application-level heartbeat. The proxy's reaper terminates any
                    // client that has not proved it is alive within one 30s tick, and
                    // through cloudflared it cannot rely on protocol ping/pong frames
                    // surviving the tunnel (index.js says so in its own comment). This
                    // reply is the proof of life that does survive, because it is an
                    // ordinary data frame — the same kind the traffic events ride on.
                    // Answer first and cheaply: it must not sit behind any of the
                    // handlers below, and it must not depend on React state.
                    if (data.type === 'ping') {
                        try {
                            if (socket.readyState === WebSocket.OPEN) {
                                socket.send(JSON.stringify({ type: 'pong' }));
                            }
                        } catch { /* a send failure is the socket dying anyway */ }
                        return;
                    }

                    if (data.type === 'server_ip') {
                        // If we are on localhost, use detected LAN IP.
                        // If we are on public domain/IP, use instead (internal IP might be wrong/inaccessible).
                        const browserHost = window.location.hostname;
                        const isLocal = browserHost === 'localhost' || browserHost === '127.0.0.1';

                        if (isLocal) {
                            setServerIp(data.ip);
                        } else {
                            setServerIp(browserHost);
                        }
                        return;
                    }

                    // Datacenter-egress self-check — sent once per connection, shortly after
                    // server_ip. Stored as-is; App.js's ribbon renders a warning chip when
                    // data.datacenter is true.
                    if (data.type === 'egress_status') {
                        setEgressStatus(data);
                        return;
                    }

                    // Config override confirmation — proxy echoes back the active override map
                    if (data.type === 'override_confirmed') {
                        // ConfigViewer manages its own override state; nothing to update here.
                        return;
                    }

                    // Intentional clear — wipe state and localStorage.
                    // Delivered per viewer by the proxy: this is OUR clear (or a clear from
                    // another tab of ours), never another user's.
                    if (classifyHistoryCleared(data) !== 'ignore') {
                        // Scoped clear: only one platform/device tab was cleared.
                        if (classifyHistoryCleared(data) === 'device') {
                            setGroupedResults(prev => {
                                if (!(data.deviceId in prev)) return prev;
                                // Delete the key (not `[id]: []`): an empty group kept the
                                // ribbon showing a "Android (0)" tab forever.
                                const next = { ...prev };
                                delete next[data.deviceId];
                                persistSlim(next);
                                if (selectedTabRef.current === data.deviceId || !(selectedTabRef.current in next)) {
                                    setSelectedTab(Object.keys(next)[0] || '');
                                }
                                return next;
                            });
                            triggerClear(); // reset right panel / observer for the cleared view
                            return;
                        }
                        resetAllLocalTraffic();
                        // After a full wipe there is nothing "earlier" left — a stale
                        // boundary would otherwise hide the very next capture behind a
                        // "Show earlier" toggle for no reason. Scoped (deviceId) clears
                        // above intentionally leave the boundary alone.
                        clearSessionBoundary();
                        // Dashboard-owned reactions (reset auto-detect / capture session UI)
                        emit(data);
                        return;
                    }

                    // Handle History Snapshot from Proxy
                    // Strategy: MERGE snapshot with existing localStorage rather than replace.
                    // This ensures:
                    //   - A proxy restart with partial history doesn't wipe frontend-only data
                    //   - Multi-device sessions survive individual device reconnects
                    //   - Dedup by requestId prevents double-entries on every reconnect
                    if (classifyHistorySnapshot(data) !== 'ignore') {
                        setHistoryLoaded(true);

                        if (classifyHistorySnapshot(data) === 'empty') {
                            // Proxy returned empty (restarted / no history file yet).
                            // Keep whatever is already in localStorage — don't wipe the user's session.
                            console.warn('⚠️ Proxy returned empty history_snapshot — keeping existing data.');
                            return;
                        }

                        // Build snapshot grouped by deviceId
                        const snapshotGrouped = {};
                        data.data.forEach(item => {
                            const key = item.deviceId || 'unknown device';
                            if (!snapshotGrouped[key]) snapshotGrouped[key] = [];
                            snapshotGrouped[key].push(item);
                        });

                        // Reconcile (not just merge) against this NON-EMPTY snapshot — the
                        // proxy's complete retained history. Any device group cached locally
                        // but ABSENT from the snapshot has been cleared/evicted on the proxy
                        // and must be dropped, or it becomes a phantom device count that
                        // survives Clear / logout-login / proxy restarts forever (the bug this
                        // fixes). Devices present in the snapshot still get the old
                        // union + dedup-by-requestId + two-tier retention treatment — no rows
                        // are lost for a live/retained device. See reconcileGroupsWithSnapshot
                        // in utils/liveHistory.js for the pure prune/merge logic (unit-tested).
                        // Wrapped so a malformed snapshot can never crash the WS handler.
                        setGroupedResults(prev => {
                            const next = reconcileGroupsWithSnapshot(prev, snapshotGrouped, applyTwoTier);
                            persistSlim(next);

                            // If the currently-selected tab's device was pruned (no longer in
                            // the snapshot), fall back to a remaining tab so the UI doesn't
                            // point at a dropped group.
                            if (selectedTabRef.current && !(selectedTabRef.current in next)) {
                                const fallback = Object.keys(next)[0] || '';
                                setSelectedTab(fallback);
                            }
                            return next;
                        });

                        // Auto-select tab if none selected yet
                        if (!selectedTabRef.current) {
                            const first = Object.keys(snapshotGrouped)[0] || selectedTab;
                            if (first) setSelectedTab(first);
                        }
                        return;
                    }

                    // Android IP takeover (proxy-server/enrollments.js): sent only to the
                    // two owners involved. An older proxy never sends it, so this is inert there.
                    if (data.type === 'ip_takeover') {
                        setTakeoverNotices(prev => applyTakeoverNotice(prev, data));
                        return;
                    }

                    // Dropped/pinned host — MITM handshake failed, no decryptable request.
                    // Surfaced so "I don't see X" can be confirmed as a TLS drop, not a miss.
                    if (data.type === 'tls_drop' && data.data && data.data.host) {
                        setTlsDrops(prev => ({ ...prev, [data.data.host]: data.data }));
                        return;
                    }

                    // Watch mode — proactive trend-anomaly alert (docs/signals-
                    // recommendations-watch-spec.md §6, watch/sinks.js's `{type:
                    // 'watch_alert', data: <alert>}` broadcast). Bounded newest-first
                    // ring, independent of the captured-row state so it's never
                    // touched by a history_snapshot merge; deduped by alert.id in
                    // case a sink ever redelivers.
                    if (data.type === 'watch_alert' && data.data) {
                        setWatchAlerts(prev => applyWatchAlert(prev, data.data));
                        return;
                    }

                    // An async Ask finished (docs/checks-ask-async-spec.md). This is
                    // the fast path only — the answer is also on the job record, and
                    // the panel falls back to fetching it by id, because a tab that
                    // reconnected after this fired never saw it.
                    if (data.type === 'checks_ask_job' && data.jobId) {
                        setAskJobs(prev => applyAskJob(prev, data));
                        return;
                    }

                    // AI analyst Phase 1b — late advisory verdict for a row that was
                    // already broadcast. Rows are append-only and deduped by requestId,
                    // so re-sending the row is dropped; instead we MERGE the verdicts
                    // onto the existing row (by requestId) at validData.aiVerdicts.
                    if (data.type === 'ai_verdict' && data.data && Array.isArray(data.data.verdicts)) {
                        const { requestId, deviceId, verdicts } = data.data;
                        if (!requestId) return;
                        setGroupedResults(prev => {
                            // deviceId narrows the search when present; else scan all groups.
                            const keys = (deviceId && prev[deviceId]) ? [deviceId] : Object.keys(prev);
                            let changed = false;
                            const next = { ...prev };
                            for (const key of keys) {
                                const group = prev[key];
                                if (!Array.isArray(group)) continue;
                                let hit = false;
                                const merged = group.map(r => {
                                    if (r.requestId !== requestId) return r;
                                    hit = true;
                                    return {
                                        ...r,
                                        validData: { ...(r.validData || {}), aiVerdicts: verdicts },
                                    };
                                });
                                if (hit) { next[key] = merged; changed = true; }
                            }
                            if (!changed) return prev;
                            persistSlim(next);
                            return next;
                        });
                        return;
                    }

                    if (!data?.type?.includes('auction') || !data?.data) return;

                    if (data.type.includes('auction')) {
                        const deviceIdForGroup = (data.data?.deviceId) || 'unknown device';
                        if (pausedDevicesRef.current[deviceIdForGroup]) {
                            setSuppressedByDevice(prev => ({
                                ...prev,
                                [deviceIdForGroup]: (prev[deviceIdForGroup] || 0) + 1
                            }));
                            return; // drop while this device is paused
                        }
                        const newEntry = {
                            ...data.data,
                            // Keep the proxy's own ISO `timestamp` (same clock as snapshot
                            // rows); arrival time is separate. Fall back to arrival only
                            // when the proxy sent none. Consumers use ts() for both types.
                            timestamp: data.data.timestamp != null ? data.data.timestamp : Date.now(),
                            receivedAt: Date.now(),
                            requestId: data.data.requestId || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
                        };

                        const groupKey = `${newEntry.deviceId || 'unknown device'}`;

                        if (newEntry.requestType === 'configuration' && newEntry.configAttributes) {
                            setDeviceConfig(prev => ({
                                ...prev,
                                [groupKey]: newEntry.configAttributes
                            }));
                        }

                        // Publisher Settings fires once at app launch — keep only the
                        // latest so relaunching the app in-session replaces the snapshot.
                        if (newEntry.requestType === 'publisher_settings' && newEntry.validData?.summary) {
                            setPubSettings(prev => ({
                                ...prev,
                                [groupKey]: newEntry.validData.summary
                            }));
                        }

                        // Dashboard-owned reactions (auto-detect app/platform from GAM +
                        // feed capture session) — run BEFORE the grouped update, exactly
                        // as the original inline order did.
                        emit(data, newEntry);

                        setGroupedResults(prev => {
                            const group = prev[groupKey] || [];
                            // Dedup: prefer requestId match (stable), fall back to timestamp+path
                            const alreadyExists = newEntry.requestId
                                ? group.some(r => r.requestId === newEntry.requestId)
                                : group.some(r => r.timestamp === newEntry.timestamp && r.path === newEntry.path);
                            if (alreadyExists) return prev;

                            if (!selectedTabRef.current || !(groupKey in prev)) {
                                setSelectedTab(groupKey);
                            }

                            const nextGroup = [newEntry, ...group];
                            // Two-tier: strip bodies from the row that just crossed out of the
                            // full-body window (O(1) per event), then cap total at HISTORY_KEEP.
                            if (nextGroup.length > FULL_BODY_KEEP
                                && nextGroup[FULL_BODY_KEEP] && !nextGroup[FULL_BODY_KEEP]._bodyStripped) {
                                nextGroup[FULL_BODY_KEEP] = stripBodies(nextGroup[FULL_BODY_KEEP]);
                            }
                            const trimmed = nextGroup.length > HISTORY_KEEP ? nextGroup.slice(0, HISTORY_KEEP) : nextGroup;
                            const next = {
                                ...prev,
                                [groupKey]: trimmed
                            };
                            persistSlim(next);
                            return next;
                        });
                    }
                } catch (err) {
                    console.error('❌ Failed to parse WebSocket message', err);
                }
            };
        }

        connect();

        if (socket && socket.readyState === WebSocket.OPEN) {
            // If session ID changes while connected, re-request history for the new session
            socket.send(JSON.stringify({ type: 'get_history', ssid: sessionId }));
        }

        return () => {
            // ... cleanup ...
            isUnmounting = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (socket) socket.close();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sessionId, proxyWsUrl // Re-run when proxy URL changes (NOT pausedDevices — read via ref)
    ]);

    return (
        <TrafficContext.Provider value={{
            // ── existing contract (unchanged shapes) ──
            liveTrafficGroups: groupedResults,
            sessionId, setSessionId,
            clearSignal, triggerClear,
            sessionBoundary, startNewSession, clearSessionBoundary,
            wsStatus: wsConnectState,
            storageNotice, dismissStorageNotice,
            proxyWsUrl,
            connectedDevices,
            jumpTarget, setJumpTarget,
            checksScopeRows, setChecksScopeRows,
            // ── live-engine state now owned here (consumed by the Dashboard) ──
            serverIp,
            egressStatus,
            tlsDrops,
            watchAlerts, dismissWatchAlert, clearWatchAlerts,
            askJobs,
            takeoverNotices, dismissTakeoverNotice,
            historyLoaded,
            deviceConfig,
            pubSettings,
            pausedDevices,
            suppressedByDevice,
            selectedTab, setSelectedTab,
            togglePauseDevice,
            wsSend,
            resetAllLocalTraffic,
            subscribeMessages,
        }}>
            {children}
        </TrafficContext.Provider>
    );
};
